import React, { useState } from "react";
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { ArrowLeft, Calculator, HelpCircle, Search, ChevronDown, ChevronRight, FileText, CreditCard, Shield, Users, AlertCircle } from "lucide-react";
import { useNavigate } from "react-router-dom";

const HelpCenter = () => {
  const navigate = useNavigate();
  const [expandedFaq, setExpandedFaq] = useState(null);
  const [searchTerm, setSearchTerm] = useState("");

  const faqData = [
    {
      category: "Getting Started",
      icon: Users,
      questions: [
        {
          q: "How do I get started with Tax Helper Pros?",
          a: "Simply create an account, upload your bank statement (PDF format), and our AI will automatically analyze and categorize your transactions. You'll receive a detailed breakdown suitable for tax preparation."
        },
        {
          q: "What file formats do you support?",
          a: "Currently, we support PDF bank statements from all major banks. We're working on adding support for CSV and Excel files in future updates."
        },
        {
          q: "Do I need to install any software?",
          a: "No! Tax Helper Pros is entirely web-based. Just access it through your browser from any device with an internet connection."
        }
      ]
    },
    {
      category: "Pricing & Billing",
      icon: CreditCard,
      questions: [
        {
          q: "How does pay-per-use pricing work?",
          a: "You pay $1 per PDF upload plus $1 per page processed. For example, a 5-page bank statement costs $6 total ($1 upload + $5 for pages). No monthly fees or subscriptions."
        },
        {
          q: "Are there any hidden fees?",
          a: "Absolutely not. Our pricing is completely transparent: $1 per PDF + $1 per page. There are no setup fees, monthly charges, or hidden costs."
        },
        {
          q: "What payment methods do you accept?",
          a: "We accept all major credit cards (Visa, MasterCard, American Express, Discover) through our secure Stripe payment processing."
        },
        {
          q: "Can I get a refund if I'm not satisfied?",
          a: "Yes! If you're not satisfied with the analysis quality, contact us within 24 hours and we'll provide a full refund, no questions asked."
        }
      ]
    },
    {
      category: "Security & Privacy",
      icon: Shield,
      questions: [
        {
          q: "How secure is my financial data?",
          a: "Your data is protected with bank-level security including 256-bit AES encryption, TLS 1.3 for transmission, and SOC 2 Type II compliance. We never store your sensitive documents."
        },
        {
          q: "Do you store my bank statements?",
          a: "No! Your documents are processed in secure memory and immediately deleted after analysis. We follow a strict \"process and delete\" policy."
        },
        {
          q: "Who has access to my data?",
          a: "Only our automated AI systems process your data. No human employees see your financial information. The processing is completely automated and secure."
        },
        {
          q: "Are you compliant with privacy regulations?",
          a: "Yes, we're fully compliant with GDPR, CCPA, and other major privacy regulations. We also maintain SOC 2 Type II and ISO 27001 certifications."
        }
      ]
    },
    {
      category: "Technical Support",
      icon: FileText,
      questions: [
        {
          q: "What if my PDF won't upload?",
          a: "Ensure your PDF is under 50MB and isn't password-protected. Try refreshing the page and uploading again. If issues persist, contact our support team."
        },
        {
          q: "The analysis seems incomplete. What should I do?",
          a: "This can happen with poor-quality scans or unusual bank statement formats. Try uploading a higher-quality PDF or contact us for manual review."
        },
        {
          q: "Can I edit the categorizations?",
          a: "Yes! After processing, you can review and modify any transaction categories before downloading your final report."
        },
        {
          q: "How accurate is the AI analysis?",
          a: "Our AI achieves over 95% accuracy in transaction categorization. However, we recommend reviewing the results before using them for tax filing."
        }
      ]
    }
  ];

  const filteredFaqs = faqData.map(category => ({
    ...category,
    questions: category.questions.filter(
      item => 
        item.q.toLowerCase().includes(searchTerm.toLowerCase()) ||
        item.a.toLowerCase().includes(searchTerm.toLowerCase())
    )
  })).filter(category => category.questions.length > 0);

  const toggleFaq = (categoryIndex, questionIndex) => {
    const key = `${categoryIndex}-${questionIndex}`;
    setExpandedFaq(expandedFaq === key ? null : key);
  };

  return (
    <div className="min-h-screen bg-gradient-to-br from-slate-50 to-blue-50">
      {/* Navigation */}
      <nav className="bg-white/80 backdrop-blur-md shadow-sm border-b sticky top-0 z-50">
        <div className="container mx-auto px-4 py-4">
          <div className="flex justify-between items-center">
            <div className="flex items-center space-x-3">
              <div className="w-10 h-10 bg-blue-600 rounded-xl flex items-center justify-center">
                <Calculator className="w-6 h-6 text-white" />
              </div>
              <span className="text-2xl font-bold text-slate-800" style={{fontFamily: 'Space Grotesk, sans-serif'}}>
                Tax Helper Pros
              </span>
            </div>
            
            <Button 
              variant="ghost" 
              onClick={() => navigate('/')}
              className="text-slate-600 hover:text-slate-800"
            >
              <ArrowLeft className="w-4 h-4 mr-2" />
              Back to Home
            </Button>
          </div>
        </div>
      </nav>

      <div className="container mx-auto px-4 py-12 max-w-4xl">
        {/* Header */}
        <div className="text-center mb-12">
          <div className="flex items-center justify-center gap-3 mb-6">
            <HelpCircle className="w-12 h-12 text-blue-600" />
            <h1 className="text-4xl font-bold text-slate-800" style={{fontFamily: 'Space Grotesk, sans-serif'}}>
              Help Center
            </h1>
          </div>
          <p className="text-lg text-slate-600">
            Find answers to common questions and get the help you need
          </p>
        </div>

        {/* Search Box */}
        <Card className="shadow-lg border-0 mb-8">
          <CardContent className="p-6">
            <div className="relative">
              <Search className="absolute left-3 top-1/2 transform -translate-y-1/2 text-slate-400 w-5 h-5" />
              <input
                type="text"
                placeholder="Search for help topics..."
                value={searchTerm}
                onChange={(e) => setSearchTerm(e.target.value)}
                className="w-full pl-10 pr-4 py-3 border border-slate-200 rounded-lg focus:outline-none focus:ring-2 focus:ring-blue-500 focus:border-transparent"
              />
            </div>
          </CardContent>
        </Card>

        {/* Quick Actions */}
        <div className="grid grid-cols-1 md:grid-cols-3 gap-6 mb-12">
          <Card className="shadow-lg border-0 hover:shadow-xl transition-shadow cursor-pointer" onClick={() => navigate('/contact')}>
            <CardContent className="p-6 text-center">
              <div className="w-12 h-12 bg-blue-100 rounded-lg flex items-center justify-center mx-auto mb-4">
                <Users className="w-6 h-6 text-blue-600" />
              </div>
              <h3 className="font-semibold text-slate-800 mb-2">Contact Support</h3>
              <p className="text-slate-600 text-sm">Get personalized help from our support team</p>
            </CardContent>
          </Card>

          <Card className="shadow-lg border-0 hover:shadow-xl transition-shadow cursor-pointer" onClick={() => navigate('/tax-guide')}>
            <CardContent className="p-6 text-center">
              <div className="w-12 h-12 bg-green-100 rounded-lg flex items-center justify-center mx-auto mb-4">
                <FileText className="w-6 h-6 text-green-600" />
              </div>
              <h3 className="font-semibold text-slate-800 mb-2">Tax Guide</h3>
              <p className="text-slate-600 text-sm">Learn tax preparation best practices</p>
            </CardContent>
          </Card>

          <Card className="shadow-lg border-0 hover:shadow-xl transition-shadow cursor-pointer" onClick={() => navigate('/api-docs')}>
            <CardContent className="p-6 text-center">
              <div className="w-12 h-12 bg-purple-100 rounded-lg flex items-center justify-center mx-auto mb-4">
                <Calculator className="w-6 h-6 text-purple-600" />
              </div>
              <h3 className="font-semibold text-slate-800 mb-2">API Documentation</h3>
              <p className="text-slate-600 text-sm">Integrate Tax Helper Pros into your workflow</p>
            </CardContent>
          </Card>
        </div>

        {/* FAQ Sections */}
        <div className="space-y-6">
          {filteredFaqs.map((category, categoryIndex) => {
            const IconComponent = category.icon;
            return (
              <Card key={categoryIndex} className="shadow-lg border-0">
                <CardHeader className="bg-slate-50 border-b">
                  <CardTitle className="flex items-center gap-3 text-slate-800">
                    <IconComponent className="w-6 h-6 text-blue-600" />
                    {category.category}
                  </CardTitle>
                </CardHeader>
                <CardContent className="p-6">
                  <div className="space-y-4">
                    {category.questions.map((item, questionIndex) => {
                      const isExpanded = expandedFaq === `${categoryIndex}-${questionIndex}`;
                      return (
                        <div key={questionIndex} className="border border-slate-200 rounded-lg">
                          <button
                            onClick={() => toggleFaq(categoryIndex, questionIndex)}
                            className="w-full p-4 text-left flex justify-between items-center hover:bg-slate-50 transition-colors"
                          >
                            <span className="font-medium text-slate-800">{item.q}</span>
                            {isExpanded ? (
                              <ChevronDown className="w-5 h-5 text-slate-500" />
                            ) : (
                              <ChevronRight className="w-5 h-5 text-slate-500" />
                            )}
                          </button>
                          {isExpanded && (
                            <div className="px-4 pb-4 text-slate-700 border-t border-slate-200 bg-slate-25">
                              <p className="pt-4">{item.a}</p>
                            </div>
                          )}
                        </div>
                      );
                    })}
                  </div>
                </CardContent>
              </Card>
            );
          })}
        </div>

        {/* Still Need Help */}
        <Card className="shadow-xl border-2 border-blue-200 bg-blue-50 mt-12">
          <CardContent className="p-6 text-center">
            <div className="flex items-center justify-center gap-3 mb-4">
              <AlertCircle className="w-8 h-8 text-blue-600" />
              <h3 className="text-2xl font-bold text-blue-800">Still Need Help?</h3>
            </div>
            <p className="text-blue-700 text-lg mb-6">
              Can't find what you're looking for? Our support team is here to help you succeed.
            </p>
            <div className="flex justify-center gap-4">
              <Button 
                onClick={() => navigate('/contact')}
                className="bg-blue-600 hover:bg-blue-700 text-white"
              >
                Contact Support
              </Button>
              <Button 
                variant="outline"
                onClick={() => window.open('mailto:support@taxhelperpros.com')}
                className="border-blue-600 text-blue-600 hover:bg-blue-50"
              >
                Email Us
              </Button>
            </div>
          </CardContent>
        </Card>

        {/* Action Buttons */}
        <div className="flex justify-center gap-4 mt-8">
          <Button 
            onClick={() => navigate('/')}
            className="bg-blue-600 hover:bg-blue-700 text-white"
          >
            Back to Home
          </Button>
          <Button 
            variant="outline"
            onClick={() => navigate('/auth')}
            className="border-blue-600 text-blue-600 hover:bg-blue-50"
          >
            Get Started
          </Button>
        </div>
      </div>
    </div>
  );
};

export default HelpCenter;